import { Box, Button, Typography, Modal } from "@material-ui/core";
import { CircularProgress, IconButton, Slider, Select, MenuItem } from "@mui/material";
import { saveAs } from "file-saver";
import JSZip from "jszip";
import {
  CornersIn,
  CornersOut,
  Export,
  Pause,
  Play,
  SkipBack,
  SkipForward,
  X,
  CaretLeft,
  CaretRight,
} from "phosphor-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { fget } from "../../../API/callsAPI.js";
import useVideoBatchLoader from "../../../Hooks/SequencerHooks/useVideoBatchLoader.js";
import captureVideoFrame from "../../Player/capture-video-frame.js";
import PlayerView from "./PlayerView.styles.js";

const embeddedVersionSelectSx = {
  width: "200px",
  height: "32px",
  bgcolor: "#1B1D28",
  color: "#F9F9F9",
  fontSize: "14px",
  borderRadius: "8px",
  padding: "6px",
  "& .MuiSelect-select": {
    py: "4px",
    px: "8px",
    color: "#F9F9F9",
  },
  "& .MuiSelect-icon": {
    color: "#F9F9F9",
  },
};

const embeddedVersionMenuProps = {
  PaperProps: {
    sx: {
      bgcolor: "#1B1D28",
      color: "#F9F9F9",
      "& .MuiMenuItem-root": {
        color: "#F9F9F9",
        bgcolor: "#1B1D28",
        fontSize: "14px",
        "&:hover": {
          bgcolor: "#252A38",
        },
        "&.Mui-selected": {
          bgcolor: "#353A47",
        },
        "&.Mui-selected:hover": {
          bgcolor: "#353A47",
        },
      },
    },
  },
};

const SequencerMixVideoPlayer = ({
  setScreenshot,
  canvasModal,
  currentTime,
  setCurrentTime,
  isVideoPlaying,
  sequencerDetails,
  projectId,
  currentUrlBatch,
  setCurrentUrlBatch,
  loadedUrls,
  orderedVideo,
  seekToTime,
  setIsVideoPlaying,
  startedLoadingBatches,
  playerContainerRef,
  forceReloadBatch,
  setSelectedVersion,
  registerVideoReset,
  setResetAll,
  isExportModalOpen,
  setIsExportModalOpen,
  isExporting,
  setIsExporting,
  embedded = false,
  setSeekToTime,
  selectedVersion,
  totalDuration,
}) => {
  const classes = PlayerView();
  const containerRef = useRef();
  const [isFullScreen, setIsFullScreen] = useState(false);

  const formatTime = (seconds) => {
    if (isNaN(seconds)) return "00:00:00";
    const h = Math.floor(seconds / 3600)
      .toString()
      .padStart(2, "0");
    const m = Math.floor((seconds % 3600) / 60)
      .toString()
      .padStart(2, "0");
    const s = Math.floor(seconds % 60)
      .toString()
      .padStart(2, "0");
    return `${h}:${m}:${s}`;
  };

  const {
    startedLoadingVideoBatches,
    currentVideoBatch,
    isNewVideoVisible,
    playPrevious,
    playNext,
    loadedVideos,
    currentVideoIndex,
    resetVideoBatchStates,
  } = useVideoBatchLoader(
    orderedVideo,
    loadedUrls,
    containerRef.current,
    isVideoPlaying,
    setCurrentTime,
    currentUrlBatch,
    setCurrentUrlBatch,
    seekToTime,
    setIsVideoPlaying,
    forceReloadBatch,
    setSelectedVersion,
    setResetAll,
    sequencerDetails.frame_rate
  );

  useEffect(() => {
    if (registerVideoReset) {
      registerVideoReset(resetVideoBatchStates);
    }
  }, [registerVideoReset, resetVideoBatchStates]);

  const handleFullScreen = () => {
    if (containerRef.current) {
      if (!document?.fullscreenElement) {
        try {
          containerRef.current.requestFullscreen();
          setIsFullScreen(true);
        } catch (error) {
          console.log(error);
        }
      } else {
        try {
          document.exitFullscreen();
          setIsFullScreen(false);
        } catch (error) {
          console.log(error);
        }
      }
    }
  };

  useEffect(() => {
    const playerContainer = playerContainerRef.current;
    if (playerContainer) {
      playerContainer.tabIndex = -1;

      const handleKeyDown = (e) => {
        const target = e.target;
        const isInputElement =
          target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.contentEditable === "true" ||
          target.closest('[contenteditable="true"]') ||
          target.closest(".DraftEditor-root");

        // If the event is from an input element, don't handle it
        if (isInputElement) {
          return;
        }

        // e.preventDefault();
        switch (true) {
          case e.key === " ":
            setIsVideoPlaying((prev) => !prev);
            break;
          case e.ctrlKey && (e.keyCode === 39 || e.key === "ArrowRight"):
            playNext();
            break;
          case e.ctrlKey && (e.keyCode === 37 || e.key === "ArrowLeft"):
            playPrevious();
            break;
          case e.key === "f":
            handleFullScreen();
            break;
          default:
            break;
        }
      };

      // Add event listener
      playerContainer.addEventListener("keydown", handleKeyDown);

      // Cleanup function to remove event listener
      return () => {
        playerContainer.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [playerContainerRef, playNext, playPrevious]);

  useEffect(() => {
    if (!canvasModal) return;

    const video = loadedVideos.current[currentVideoIndex];
    if (!video) return;

    try {
      const frame = captureVideoFrame(video);
      if (frame?.dataUri) {
        setScreenshot(frame.dataUri);
      }
    } catch (error) {
      console.error("Failed to capture video frame:", error);
      toast.error("Unable to capture frame from video");
    }
  }, [canvasModal, currentVideoIndex, setScreenshot]);

  async function fetchShotSources(shotList) {
    const queryParam = shotList.map((eachShot) => `shot_version=${eachShot.shotVersion.id}`);
    const signedUrlObj = {};
    try {
      const query = queryParam.join("&");
      const signedUrls = await fget({
        url: `sequencers/timelines/${sequencerDetails.id}/files?${query}`,
      });
      if (signedUrls.data?.results?.length) {
        for (let i = 0; i < signedUrls.data?.results?.length; i++) {
          signedUrlObj[signedUrls.data?.results[i].shot_version] =
            signedUrls.data?.results[i];
        }
      }
    } catch (error) {
      console.log(error);
    }
    return Promise.resolve(signedUrlObj);
  }

  const handleExport = async () => {
    //step1: create EDL File
    //step2: download video files
    //step3: create zip file using edl file and video files and download it
    setIsExporting(true);
    const allSignedUrls = await fetchShotSources(orderedVideo);
    const edlFile = createEDLFile(orderedVideo);
    exportProject(orderedVideo, edlFile, allSignedUrls);
  }

  async function exportProject(videos, edlContent, allSignedUrls) {
    const zip = new JSZip();

    zip.file(`${sequencerDetails?.name}.edl`, edlContent);

    const fetchResults = await Promise.allSettled(
      videos.map(async (video) => {
        const urlData = allSignedUrls[video.shotVersion.id];

        if (!urlData) {
          console.warn("Video not found in loadedUrls:", video.shotVersion.id);
          return { skipped: true, id: video.shotVersion.id };
        }

        const response = await fetch(urlData.signed_url);

        if (!response.ok) {
          throw new Error(
            `Failed to fetch video ${video.shotVersion.name}: ${response.status} ${response.statusText}`
          );
        }

        const blob = await response.blob();
        return { blob, name: video.shotVersion.name };
      })
    );

    const errors = [];

    for (const result of fetchResults) {
      if (result.status === "fulfilled") {
        const { blob, name, skipped } = result.value;
        if (!skipped) {
          zip.file(`videos/${name}.mp4`, blob);
        }
      } else {
        console.error("Video fetch failed:", result.reason);
        errors.push(result.reason?.message ?? "Unknown error");
      }
    }

    if (errors.length > 0) {
      console.error(`${errors.length} video(s) failed to export:`, errors);
      toast.error(`${errors.length} video(s) failed to download`);
    }

    try {
      const zipBlob = await zip.generateAsync({ type: "blob" });
      saveAs(zipBlob, `${sequencerDetails?.name}.zip`);
    } catch (err) {
      console.error("Failed to generate zip:", err);
      toast.error("Export failed — could not generate zip file");
      throw err;
    } finally {
      setIsExporting(false);
      setIsExportModalOpen(false);
    }
  }

  function frameToTimecode(frame, fps) {
    const totalSeconds = Math.floor(frame / fps);
    const frames = frame % fps;

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const pad = (n) => String(n).padStart(2, "0");

    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}:${pad(frames)}`;
  }

  const createEDLFile = (orderedVideo) => {
    //edl header
    let edlContent = `TITLE: Project Export - ${sequencerDetails?.name}\n`
    edlContent += "FCM: NON-DROP FRAME\n";
    edlContent += "\n";

    orderedVideo.forEach((video, index) => {

      const sourceIn = frameToTimecode(0, sequencerDetails.frame_rate);
      const sourceOut = frameToTimecode(video.fpsEnd - video.fpsStart, sequencerDetails.frame_rate);

      const timelineIn = frameToTimecode(video.fpsStart, sequencerDetails.frame_rate);
      const timelineOut = frameToTimecode(video.fpsEnd, sequencerDetails.frame_rate);

      const sourceNumber = (index + 1).toString().padStart(3, '0');
      const sourceName = `CLIP${sourceNumber}`;
      edlContent += `${sourceNumber}  ${sourceName}  V  C  ${sourceIn} ${sourceOut} ${timelineIn} ${timelineOut}\n`;
      // Comment lines with metadata
      edlContent += `* FROM CLIP NAME: videos/${video.shotVersion.name}.mp4\n`;
    });
    return edlContent;
  }

  return (
    <Box
      className={
        embedded ? classes.sequencerPlayerRootEmbedded : classes.sequencerPlayerRoot
      }
    >
      <Box
        className={
          embedded ? classes.playerContainerEmbedded : classes.playerContainer
        }
      >
        {orderedVideo.length === 0 && (
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "#000000",
            }}
          />
        )}
        {orderedVideo.length > 0 && (
          <Box
            style={{
              position: "absolute",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              width: "100%",
              zIndex: 10,
              backgroundColor: "#000000",
              opacity:
                startedLoadingVideoBatches.has(currentVideoBatch) ||
                  startedLoadingBatches.has(currentUrlBatch) ||
                  !isNewVideoVisible
                  ? 1
                  : 0,
              transition: "opacity 0.3s ease-in-out",
              pointerEvents:
                startedLoadingVideoBatches.has(currentVideoBatch) ||
                  startedLoadingBatches.has(currentUrlBatch) ||
                  !isNewVideoVisible
                  ? "auto"
                  : "none",
            }}
          >
            <CircularProgress size={130} />
          </Box>
        )}
        {orderedVideo.length > 0 && (
          <Box
            ref={containerRef}
            key="video-container"
            style={{
              zIndex: 1,
              position: "absolute",
              width: "100%",
              height: "100%",
            }}
          />
        )}
      </Box>
      <Box
        className={
          embedded
            ? classes.sequencerVideoControlsEmbedded
            : classes.sequencerVideoControls
        }
      >
        {embedded ? (
          <>
            <Box className={classes.embeddedSeekbarRow}>
              <Box style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <IconButton
                  className={classes.playerButton}
                  onClick={() => {
                    playPrevious();
                  }}
                >
                  <CaretLeft size={14} color="#F9F9F9" />
                </IconButton>
                <Select
                  sx={embeddedVersionSelectSx}
                  MenuProps={embeddedVersionMenuProps}
                  value={selectedVersion?.shotVersion?.id}
                  onChange={(e) => {
                    const selectedShot = orderedVideo.find((shot) => shot.shotVersion.id === e.target.value);
                    setSelectedVersion(selectedShot);
                    setCurrentTime(selectedShot.previousEnd);
                    setSeekToTime(selectedShot.previousEnd);
                  }}
                >
                  {orderedVideo.map((shot) => (
                    <MenuItem value={shot.shotVersion.id}>{shot.shotVersion.name}</MenuItem>
                  ))}
                </Select>
                <IconButton
                  className={classes.playerButton}
                  onClick={() => {
                    playNext();
                  }}
                >
                  <CaretRight size={14} color="#F9F9F9" />
                </IconButton>
              </Box>
              <Slider
                value={currentTime}
                min={0}
                max={totalDuration}
                step={1}
                onChange={(e, value) => {
                  setIsVideoPlaying(false);
                  setSeekToTime(value);
                  setCurrentTime(value);
                }}
                marks={orderedVideo.map((video) => ({
                  value: video.previousEnd,
                }))}
                sx={{
                  flex: 1,
                  color: "#6C65D9",
                  height: 4,
                  padding: 0,
                  "& .MuiSlider-mark": {
                    backgroundColor: "#52CBFF !important",

                  },
                  "& .MuiSlider-markActive": {
                    color: "#6C65D9 !important",
                  },
                }}
              />
            </Box>
            <Box className={classes.embeddedTransportRow}>
              <Typography className={classes.backButton}>
                <span className={classes.playlistText}>Playlist</span>
                {formatTime(currentTime)}{" "}
                <span className={classes.totalDuration}>
                  /{" "}
                  {formatTime(totalDuration)}
                </span>
              </Typography>
              <Box style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <SkipBack size={20} color="#B1B5C9" weight="fill" />
                <Button
                  disabled={orderedVideo.length === 0}
                  onClick={() => setIsVideoPlaying(!isVideoPlaying)}
                >
                  {!isVideoPlaying ? (
                    <Play size={20} weight="fill" color="#F9F9F9" />
                  ) : (
                    <Pause size={20} weight="fill" color="#F9F9F9" />
                  )}
                </Button>
                <SkipForward size={20} color="#B1B5C9" weight="fill" />
                <Button
                  disabled={orderedVideo.length === 0}
                  onClick={handleFullScreen}
                >
                  {isFullScreen ? (
                    <CornersIn weight="fill" fill="#B1B5C9" size={20} />
                  ) : (
                    <CornersOut weight="fill" fill="#B1B5C9" size={20} />
                  )}
                </Button>
              </Box>
            </Box>
          </>
        ) : (
          <Box
            className={classes.flexBox}
          >
            <Typography className={classes.backButton}>
              <span className={classes.playlistText}>Playlist</span>
              {formatTime(currentTime)}{" "}
              <span className={classes.totalDuration}>
                /{" "}
                {formatTime(totalDuration)}
              </span>
            </Typography>
            <Box style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <SkipBack size={20} color="#B1B5C9" weight="fill" />
              <Button
                disabled={orderedVideo.length === 0}
                onClick={() => setIsVideoPlaying(!isVideoPlaying)}
              >
                {!isVideoPlaying ? (
                  <Play size={20} weight="fill" color="#F9F9F9" />
                ) : (
                  <Pause size={20} weight="fill" color="#F9F9F9" />
                )}
              </Button>
              <SkipForward size={20} color="#B1B5C9" weight="fill" />
            </Box>
            <Button
              disabled={orderedVideo.length === 0}
              onClick={handleFullScreen}
            >
              {isFullScreen ? (
                <CornersIn weight="fill" fill="#B1B5C9" size={20} />
              ) : (
                <CornersOut weight="fill" fill="#B1B5C9" size={20} />
              )}
            </Button>
          </Box>)}
      </Box>
      <Modal
        open={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box sx={{ width: "400px", height: "180px", backgroundColor: "#252A38", borderRadius: "10px", padding: "20px", display: "flex", flexDirection: "column", gap: "10px", paddingBottom: "10px" }}>
          <Typography variant="h6" align="center">Export</Typography>
          <Typography variant="body1" align="center">Are you sure you want to export this playlist with {orderedVideo.length} videos?</Typography>
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: "10px" }}>
            <Button
              variant="contained"
              onClick={handleExport}
              disabled={isExporting}
              className={classes.exportButton}
              startIcon={isExporting ? <CircularProgress size={18} /> : <Export size={18} />}
            >
              {isExporting ? "Exporting..." : "Export"}
            </Button>
            <Button variant="outlined" onClick={() => setIsExportModalOpen(false)}
              startIcon={<X size={18} />}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default SequencerMixVideoPlayer;
